import type { UserWorkspace } from "@v2/features/dashboard/sidebar/utils/get-project-display-name";
import type { ReploProject } from "schemas/generated/project";

import * as React from "react";
import { useEffect, useState } from "react";

import { Badge } from "@replo/design-system/components/badge/Badge";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@replo/design-system/components/command/Command";
import { PopoverContent } from "@replo/design-system/components/popover/Popover";
import { useSidebar } from "@replo/design-system/components/sidebar/Sidebar";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from "@replo/design-system/components/tooltip/Tooltip";
import { useOverflowDetection } from "@replo/design-system/hooks/use-overflow-detection";
import twMerge from "@replo/design-system/utils/twMerge";
import LogoBadge from "@v2/components/LogoBadge";
import { getProjectDisplayName } from "@v2/features/dashboard/sidebar/utils/get-project-display-name";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { getProjectName } from "@v2/utils/project-utils";
import { routes } from "@v2/utils/router";
import {
  Check,
  ChevronRight,
  PencilRuler,
  Plus,
  Repeat,
  Search,
  Settings,
  UserRoundPlus,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { generatePath, useNavigate } from "react-router-dom";

function getProjectNavigationPath({
  project,
  hostingType,
}: {
  project: {
    id: string;
    agentEnabledAt?: Date | null;
  };
  hostingType: "replo" | "shopify";
}): string {
  if (hostingType !== "replo") {
    return generatePath(routes.shops.site, { projectId: project.id });
  }
  const homeRoute = project.agentEnabledAt
    ? routes.agent.home
    : routes.shops.home;
  return generatePath(homeRoute, { projectId: project.id });
}

const ProjectMenuItem = ({
  project,
  isCurrentProject,
  onSelect,
}: {
  project: ReploProject;
  isCurrentProject: boolean;
  onSelect: () => void;
}) => {
  const isV2Project = project.hostingType === "replo";

  return (
    <CommandItem
      value={`${getProjectName(project)}-${project.id}`}
      startEnhancer={
        <Badge size="base">
          {isV2Project ? (
            <LogoBadge className="size-5!" />
          ) : (
            <PencilRuler className="w-full h-full" />
          )}
        </Badge>
      }
      label={getProjectName(project)}
      selected={isCurrentProject}
      endEnhancer={isCurrentProject ? <Check /> : null}
      onSelect={onSelect}
    />
  );
};

const ProjectMenu = ({
  selectedWorkspace,
  setIsCreateShopModalOpen,
  currentProject,
  setIsOpen,
}: {
  selectedWorkspace?: UserWorkspace;
  setIsCreateShopModalOpen: (isCreateShopModalOpen: boolean) => void;
  currentProject?: ReploProject;
  setIsOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  const [projectNameOverflowRef, isProjectNameOverflowing] =
    useOverflowDetection();

  const onProjectClick = (project: ReploProject) => {
    navigate(
      getProjectNavigationPath({
        project,
        hostingType: project.hostingType,
      }),
    );

    logEvent("dashboard.shop.select", {
      toShopName: project.name ?? "",
      toShopId: project.id,
    });

    setIsOpen(false);
  };

  const sortedProjects = React.useMemo(() => {
    if (!selectedWorkspace?.projects) {
      return undefined;
    }

    if (!currentProject) {
      return selectedWorkspace.projects;
    }

    // NOTE (Ben, 2026-02-23): Move the current project to the front.
    const projects = [...selectedWorkspace.projects];

    const current = projects.find((p) => p.id === currentProject.id);

    return current
      ? [current, ...projects.filter((p) => p.id !== currentProject.id)]
      : projects;
  }, [selectedWorkspace?.projects, currentProject]);

  return (
    <>
      <div className="flex flex-col gap-2 p-3 border-b-0.5 border-border">
        <TooltipRoot
          disabled={!isProjectNameOverflowing || !selectedWorkspace}
          disableHoverablePopup
        >
          <TooltipTrigger
            className="min-w-0 w-full"
            UNSAFE_className="block text-left"
          >
            <span
              ref={projectNameOverflowRef}
              className="header-h6 line-clamp-1 truncate min-w-0 block"
            >
              {selectedWorkspace ? (
                getProjectDisplayName(selectedWorkspace, currentProject)
              ) : (
                <Skeleton className="w-20 h-4" />
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">
            {selectedWorkspace
              ? getProjectDisplayName(selectedWorkspace, currentProject)
              : undefined}
          </TooltipContent>
        </TooltipRoot>

        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            startEnhancer={<Settings />}
            onClick={() => {
              navigate(
                currentProject
                  ? `/shops/${currentProject.id}/settings/workspace/general`
                  : `/workspace/${selectedWorkspace?.workspaceId}/settings/general`,
              );
              setIsOpen(false);
            }}
          >
            Workspace Settings
          </Button>
          <Button
            size="sm"
            variant="secondary"
            startEnhancer={<UserRoundPlus />}
            onClick={() => {
              navigate(
                currentProject
                  ? `/shops/${currentProject.id}/settings/workspace/members`
                  : `/workspace/${selectedWorkspace?.workspaceId}/settings/workspace/members`,
              );
              setIsOpen(false);
            }}
          >
            Invite
          </Button>
        </div>
      </div>

      <Command UNSAFE_className="p-0 rounded-none gap-0">
        <CommandInput
          placeholder="Search projects..."
          startEnhancer={<Search />}
          UNSAFE_className="m-2 mb-0 w-auto"
          autoFocus
        />
        <CommandList>
          <CommandEmpty />
          <CommandGroup UNSAFE_className="p-2">
            <div className="flex flex-col gap-0.5">
              <CommandItem
                value="new project"
                startEnhancer={
                  <Badge
                    size="base"
                    className="border-dashed border-1 bg-sheet"
                  >
                    <Plus />
                  </Badge>
                }
                label="New Project"
                onSelect={() => {
                  setIsCreateShopModalOpen(true);
                  setIsOpen(false);
                }}
                data-testid={`new-shop-${selectedWorkspace?.workspaceId}`}
              />

              {sortedProjects
                ? sortedProjects.map((project) => (
                    <ProjectMenuItem
                      key={project.id}
                      project={project}
                      isCurrentProject={project.id === currentProject?.id}
                      onSelect={() => onProjectClick(project)}
                    />
                  ))
                : Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={index} className="h-8 w-full" />
                  ))}
            </div>
          </CommandGroup>
        </CommandList>
      </Command>
    </>
  );
};

const WorkspaceMenu = ({
  workspaces,
  currentWorkspaceId,
  setSelectedWorkspaceId,
  setIsOpen,
}: {
  workspaces?: UserWorkspace[];
  currentWorkspaceId: string;
  setSelectedWorkspaceId: (workspaceId: string) => void;
  setIsOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();

  const sortedWorkspaces = React.useMemo(() => {
    if (!workspaces) {
      return undefined;
    }

    if (!currentWorkspaceId) {
      return workspaces;
    }

    // NOTE (Ben, 2026-02-23): Move the current workspace to the front.
    const workspacesList = [...workspaces];
    const current = workspacesList.find(
      (w) => w.workspaceId === currentWorkspaceId,
    );

    return current
      ? [
          current,
          ...workspacesList.filter((w) => w.workspaceId !== currentWorkspaceId),
        ]
      : workspacesList;
  }, [workspaces, currentWorkspaceId]);

  return (
    <>
      <Command UNSAFE_className="p-0 rounded-none gap-0">
        <CommandInput
          placeholder="Search workspaces..."
          startEnhancer={<Search />}
          UNSAFE_className="m-2 mb-0 w-auto"
          autoFocus
        />
        <CommandList>
          <CommandEmpty />
          <CommandGroup UNSAFE_className="p-2">
            {sortedWorkspaces
              ? sortedWorkspaces.map((workspace) => (
                  <CommandItem
                    key={workspace.workspaceId}
                    value={workspace.workspaceName}
                    size="lg"
                    label={workspace.workspaceName}
                    description={`${workspace.projects.length} ${workspace.projects.length !== 1 ? "projects" : "project"}`}
                    endEnhancer={
                      workspace.projects.length > 1 ? <ChevronRight /> : null
                    }
                    onSelect={() => {
                      if (
                        workspace.projects.length <= 1 &&
                        workspace.projects[0]
                      ) {
                        navigate(
                          getProjectNavigationPath({
                            project: workspace.projects[0],
                            hostingType: workspace.projects[0].hostingType,
                          }),
                        );
                        setIsOpen(false);
                      } else {
                        setSelectedWorkspaceId(workspace.workspaceId);
                      }
                    }}
                    UNSAFE_className="items-center"
                  />
                ))
              : Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} className="h-12 w-full" />
                ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </>
  );
};

const SLIDE_OFFSET = 16;
const SLIDE_DURATION = 0.1;

const slideVariants = {
  enter: (direction: number) => ({ x: direction * SLIDE_OFFSET, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction * -SLIDE_OFFSET, opacity: 0 }),
};

const ProjectSelectorMenuContent = ({
  currentWorkspaceId,
  workspaces,
  setIsCreateShopModalOpen,
  setIsAddWorkspaceModalOpen,
  currentProject,
  isOpen,
  setIsOpen,
}: {
  currentWorkspaceId: string;
  workspaces?: UserWorkspace[];
  setIsCreateShopModalOpen: (isCreateShopModalOpen: boolean) => void;
  setIsAddWorkspaceModalOpen: (isAddWorkspaceModalOpen: boolean) => void;
  currentProject?: ReploProject;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    currentWorkspaceId,
  );
  const slideDirectionRef = React.useRef<1 | -1>(1);
  const { isMobile } = useSidebar();

  // NOTE (Ben, 2026-02-19): Reset the selected workspace when the popover is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedWorkspaceId(currentWorkspaceId);
      slideDirectionRef.current = 1;
    }
  }, [isOpen, currentWorkspaceId]);

  const selectedWorkspace = workspaces?.find(
    (workspace) => workspace.workspaceId === selectedWorkspaceId,
  );

  const handleSwitchWorkspace = () => {
    slideDirectionRef.current = -1;
    setSelectedWorkspaceId(null);
  };

  const handleSelectWorkspace = (workspaceId: string) => {
    slideDirectionRef.current = 1;
    setSelectedWorkspaceId(workspaceId);
  };

  const handleNewWorkspace = () => {
    setIsAddWorkspaceModalOpen(true);
    setIsOpen(false);
  };

  return (
    <PopoverContent
      align="start"
      alignOffset={isMobile ? 0 : -12}
      side={isMobile ? "bottom" : "right"}
      sideOffset={isMobile ? 0 : 16}
      showCloseButton={false}
      UNSAFE_className={twMerge(
        "p-0 gap-0 overflow-hidden max-h-75",
        isMobile ? "max-w-[70vw]" : "w-62",
      )}
    >
      <AnimatePresence
        mode="wait"
        custom={slideDirectionRef.current}
        initial={false}
      >
        {selectedWorkspaceId ? (
          <motion.div
            key="project-menu"
            className="min-h-0 flex flex-col overflow-hidden"
            custom={slideDirectionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: SLIDE_DURATION, ease: "easeOut" }}
          >
            <ProjectMenu
              currentProject={currentProject}
              selectedWorkspace={selectedWorkspace}
              setIsCreateShopModalOpen={setIsCreateShopModalOpen}
              setIsOpen={setIsOpen}
            />
          </motion.div>
        ) : (
          <motion.div
            key="workspace-menu"
            className="min-h-0 flex flex-col overflow-hidden"
            custom={slideDirectionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: SLIDE_DURATION, ease: "easeOut" }}
          >
            <WorkspaceMenu
              workspaces={workspaces}
              currentWorkspaceId={currentWorkspaceId}
              setSelectedWorkspaceId={handleSelectWorkspace}
              setIsOpen={setIsOpen}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="sticky bottom-0 border-t-0.5 border-border px-5 py-2 bg-white">
        <Button
          variant="link"
          size="sm"
          startEnhancer={selectedWorkspaceId ? <Repeat /> : <Plus />}
          onClick={
            selectedWorkspaceId ? handleSwitchWorkspace : handleNewWorkspace
          }
        >
          {selectedWorkspaceId ? "Switch Workspace" : "New Workspace"}
        </Button>
      </div>
    </PopoverContent>
  );
};

export default ProjectSelectorMenuContent;
